import { LocalStorageService } from '../services/local-storage.service';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Renderer2,
} from '@angular/core';

/**
 * A drag handle on a panel edge.
 *
 * The width lives in a CSS custom property on :root rather than an inline style
 * on the host, so sibling rules can derive their geometry from the same value.
 * Writing the variable also means a component that is recreated comes back at
 * the width the user left.
 *
 * Drag moves run outside Angular: a pointermove writes one CSS property and has
 * no bearing on any binding, so paying for change detection per pixel would buy
 * nothing.
 */
@Directive({ selector: '[appResizeX]' })
export class ResizeXDirective implements AfterViewInit, OnChanges, OnDestroy {
  /** CSS custom property to write, e.g. '--ws-panel-w'. Empty disables resize. */
  @Input('appResizeX') varName = '';
  @Input() resizeMin = 180;
  /** Zero means no component-specific maximum; the viewport remains the hard
   *  physical boundary. */
  @Input() resizeMax = 720;
  /** Space at the opposite viewport edge that this panel may never cover. */
  @Input() resizeViewportInset = 0;
  /** Which edge carries the handle. A left-docked panel grows by dragging its
   *  right edge; a right-docked drawer grows by dragging its LEFT edge, where
   *  the same pointer movement must shrink-not-grow — the sign flips with it. */
  @Input() resizeEdge: 'left' | 'right' = 'right';
  /**
   * Who the remembered width belongs to. Empty means "this variable, globally"
   * — the original behaviour, which the work-item drawer still wants: one
   * drawer width across the app.
   *
   * Master/detail pages pass a scope because they share one CSS variable but do
   * not want one vertical's preferred width to change another's.
   */
  @Input() resizeScope = '';

  private readonly sessionSubscription;
  private viewReady = false;
  private handle?: HTMLElement;
  private startX = 0;
  private startWidth = 0;
  private readonly onMove = (e: PointerEvent) => this.move(e);
  private readonly onUp = (e: PointerEvent) => this.end(e);

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
    private readonly storage: LocalStorageService,
  ) {
    this.sessionSubscription = storage.sessionChanged$.subscribe(() => {
      this.detach();
      this.sync();
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.sync();
  }

  /** The host may become resizable long after it was created, so the handle is
   *  attached and detached from the input rather than once at init. */
  ngOnChanges(): void {
    this.sync();
  }

  ngOnDestroy(): void {
    this.sessionSubscription.unsubscribe();
    this.detach();
    this.removeHandle();
  }

  private sync(): void {
    if (!this.viewReady) return;

    if (!this.varName) {
      this.removeHandle();
      return;
    }

    // Re-read on every change, not once: `resizeScope` can move with the route.
    // The else branch is load-bearing for that — leaving the previous page's
    // inline width in place would show the new page at a width it never had.
    const saved = readSaved(this.storage, this.varName, this.resizeScope);
    if (saved !== null) this.write(this.clamp(saved));
    else document.documentElement.style.removeProperty(this.varName);

    if (this.handle) return;

    const handle = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(handle, 'resize-x-handle');
    if (this.resizeEdge === 'left') this.renderer.addClass(handle, 'resize-x-handle--left');
    this.renderer.setAttribute(handle, 'role', 'separator');
    this.renderer.setAttribute(handle, 'aria-orientation', 'vertical');
    this.renderer.setAttribute(handle, 'aria-label', 'Resize panel');
    this.renderer.setAttribute(handle, 'title', 'Drag to resize');
    this.renderer.appendChild(this.host.nativeElement, handle);
    this.handle = handle;

    this.zone.runOutsideAngular(() => {
      handle.addEventListener('pointerdown', (e) => this.begin(e as PointerEvent));
    });
  }

  private removeHandle(): void {
    this.handle?.remove();
    this.handle = undefined;
  }

  private detach(): void {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    document.body.classList.remove('resizing-x');
  }

  private begin(e: PointerEvent): void {
    e.preventDefault();          // no text selection while dragging
    e.stopPropagation();         // the panel behind must not treat this as a click
    this.startX = e.clientX;
    this.startWidth = this.host.nativeElement.getBoundingClientRect().width;
    document.body.classList.add('resizing-x');
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
  }

  private move(e: PointerEvent): void {
    const delta = e.clientX - this.startX;
    this.write(this.clamp(this.startWidth + (this.resizeEdge === 'right' ? delta : -delta)));
  }

  private end(e: PointerEvent): void {
    this.move(e);
    this.detach();
    // Persisted on release, not per pixel: a drag is one decision, and writing
    // localStorage on every pointermove is a synchronous write per frame.
    try {
      this.storage.setItem(storageKey(this.varName, this.resizeScope), String(this.current()));
    } catch {
      // Private mode / quota. The width still applies for this session.
    }
  }

  private current(): number {
    return Math.round(this.host.nativeElement.getBoundingClientRect().width);
  }

  private clamp(px: number): number {
    const viewportCeiling = Math.max(0, window.innerWidth - Math.max(0, this.resizeViewportInset));
    const configuredCeiling = this.resizeMax > 0 ? this.resizeMax : Number.POSITIVE_INFINITY;
    const ceiling = Math.min(configuredCeiling, viewportCeiling);
    const floor = Math.min(this.resizeMin, ceiling);
    return Math.round(Math.max(floor, Math.min(ceiling, px)));
  }

  private write(px: number): void {
    document.documentElement.style.setProperty(this.varName, `${px}px`);
  }
}

/** Unscoped for an app-wide panel, or scoped for a vertical's split. */
function storageKey(varName: string, scope = ''): string {
  return scope ? `ws-width${varName}@${scope}` : `ws-width${varName}`;
}

/** A stored width, or null when absent or unusable. */
function readSaved(storage: LocalStorageService, varName: string, scope = ''): number | null {
  try {
    const raw = storage.getItem(storageKey(varName, scope));
    if (raw === null) return null;
    const px = Number(raw);
    return Number.isFinite(px) && px > 0 ? px : null;
  } catch {
    return null;
  }
}
